import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth, type ZifekUser } from '@/hooks/useAuth';
import {
  loadPays,
  loadVilles,
  resolvePaysName,
  resolveVilleName,
  villesForPays,
  type PaysOption,
  type VilleOption,
} from '@/lib/geo';

interface Props {
  user: ZifekUser;
}

// Supprime les doublons de villes (même nom) et trie alphabétiquement.
function dedupeVilles(villes: VilleOption[]): VilleOption[] {
  const seen = new Set<string>();
  return villes
    .filter((v) => {
      const key = (v.nom_ville || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.nom_ville.localeCompare(b.nom_ville, 'fr'));
}

/**
 * Carte « Ma localisation » : permet au client de choisir son Pays et sa Ville.
 * Ces informations sont enregistrées sur son compte et servent à l'assistant IA
 * pour répondre en tenant compte de la ville de l'utilisateur.
 */
export default function ProfileLocationCard({ user }: Props) {
  const { updateUser } = useAuth();
  const [paysList, setPaysList] = useState<PaysOption[]>([]);
  const [villesList, setVillesList] = useState<VilleOption[]>([]);
  const [pays, setPays] = useState('');
  const [ville, setVille] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [paysData, villesData] = await Promise.all([loadPays(), loadVilles()]);
        if (cancelled) return;
        setPaysList(paysData);
        setVillesList(villesData);
        setPays(resolvePaysName(user.Pays, paysData));
        setVille(resolveVilleName(user.Ville, villesData));
      } catch {
        if (!cancelled) setError('Impossible de charger les pays et villes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.Pays, user.Ville]);

  const villesDisponibles = useMemo(
    () => dedupeVilles(villesForPays(pays, paysList, villesList)),
    [pays, paysList, villesList],
  );

  const dirty = pays !== resolvePaysName(user.Pays, paysList) || ville !== resolveVilleName(user.Ville, villesList);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { error: dbError } = await supabase
        .from('users')
        .update({ Pays: pays, Ville: ville })
        .eq('id', user.id);
      if (dbError) throw dbError;
      updateUser({ Pays: pays, Ville: ville });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("La sauvegarde a échoué. Réessayez dans un instant.");
    } finally {
      setSaving(false);
    }
  };

  const selectClass =
    'w-full h-11 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="bg-background-50 border border-background-200/30 rounded-xl overflow-hidden mt-5">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-background-200/30">
        <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-map-pin-2-line text-lg"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold font-heading text-foreground-950">Ma localisation</h3>
          <p className="text-xs text-foreground-500">
            Indiquez votre pays et votre ville : l&apos;assistant IA s&apos;en servira pour vous répondre localement.
          </p>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1.5">
                  Pays
                </label>
                <select
                  value={pays}
                  onChange={(e) => { setPays(e.target.value); setVille(''); }}
                  className={selectClass}
                >
                  <option value="">Sélectionnez un pays</option>
                  {paysList.map((p) => (
                    <option key={p.id} value={p.nom_pays}>{p.nom_pays}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1.5">
                  Ville
                </label>
                <select
                  value={ville}
                  onChange={(e) => setVille(e.target.value)}
                  disabled={!pays || villesDisponibles.length === 0}
                  className={selectClass}
                >
                  <option value="">
                    {!pays ? "Choisissez d'abord un pays" : villesDisponibles.length === 0 ? 'Aucune ville disponible' : 'Sélectionnez une ville'}
                  </option>
                  {villesDisponibles.map((v) => (
                    <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg mt-4">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-40 transition-all"
              >
                <i className={`ri-${saving ? 'loader-4-line animate-spin' : 'save-3-line'} text-sm`}></i>
                {saving ? 'Enregistrement...' : 'Enregistrer ma localisation'}
              </button>
              {saved && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                  <i className="ri-checkbox-circle-line"></i>
                  Localisation enregistrée
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}